import { memo, useState, type ReactNode } from "react";
import { Application } from "@/types/application";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatCurrency, formatPtpDate } from "@/utils/formatters";
import { Phone, Check, ChevronDown, ChevronRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import CallButton from "../../CallButton";
import LoanTypeBadge from "../../LoanTypeBadge";
import AudioPlayer from "@/components/ui/AudioPlayer";
import { useAuth } from "@/hooks/useAuth";

// Helper function to get tag initials and colors
// Note: we skip repo/repo-sold here to avoid duplicate badges (vehicle_status badge already shows them)
const getTagInitials = (tag: string): { initials: string; color: string; bgColor: string } | null => {
  const normalizedTag = String(tag).toLowerCase().trim();
  
  // Skip repo / repo-sold so we don't duplicate the vehicle badge
  if (
    normalizedTag === 'repo' ||
    normalizedTag === 'repossessed' ||
    normalizedTag === 'repo-sold' ||
    normalizedTag === 'repossessed_sold' ||
    normalizedTag === 'repossessed-sold'
  ) {
    return null;
  }

  if (normalizedTag === 'problematic') {
    return { initials: 'P', color: 'text-white', bgColor: 'bg-red-700 border-red-800' };
  }
  if (normalizedTag === '3rd-party' || normalizedTag === 'third party' || normalizedTag === '3rd party' || normalizedTag === 'thirdparty') {
    return { initials: '3rd', color: 'text-blue-800', bgColor: 'bg-blue-100 border-blue-200' };
  }
  
  return null;
};

// Helper function to get vehicle status tag info
const getVehicleStatusTagInfo = (vehicleStatus?: string | null): { initials: string; color: string; bgColor: string; title: string } | null => {
  if (!vehicleStatus) return null;
  
  const normalizedStatus = String(vehicleStatus).toLowerCase().trim();
  
  if (normalizedStatus === 'repossessed' || normalizedStatus === 'repo') {
    return { initials: 'R', color: 'text-green-800', bgColor: 'bg-green-100 border-green-200', title: 'Repo' };
  }
  if (normalizedStatus === 'repossessed_sold' || normalizedStatus === 'repossessed-sold' || normalizedStatus === 'repo-sold' || normalizedStatus === 'reposold') {
    return { initials: 'RS', color: 'text-purple-800', bgColor: 'bg-purple-100 border-purple-200', title: 'Repo-Sold' };
  }
  if (normalizedStatus === 'third party' || normalizedStatus === '3rd-party' || normalizedStatus === '3rd party') {
    return { initials: '3rd', color: 'text-blue-800', bgColor: 'bg-blue-100 border-blue-200', title: '3rd Party' };
  }
  
  return null;
};

function formatOverdue(value: number | null | undefined) {
  if (value == null || Number.isNaN(value)) return 'N/A';
  return `₹${value.toLocaleString('en-IN')}`;
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] leading-4 text-gray-500">{label}</div>
      <div className="text-sm font-medium leading-5 text-gray-900 break-words">{children}</div>
    </div>
  );
}

interface MobileApplicationCardProps {
  application: Application;
  onRowClick: (application: Application) => void;
  selectedApplicationId?: string;
  selectedMonth: string;
}

const getStatusColor = (status: string) => {
  const variants = {
    'Unpaid': 'bg-red-100 text-red-800 border-red-200',
    'Partially Paid': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'Cash Collected from Customer': 'bg-orange-100 text-orange-800 border-orange-200',
    'Customer Deposited to Bank': 'bg-indigo-100 text-indigo-800 border-indigo-200',
    'Paid (Pending Approval)': 'bg-blue-100 text-blue-800 border-blue-200',
    'Paid': 'bg-green-100 text-green-800 border-green-200'
  };
  
  return `${variants[status as keyof typeof variants] || 'bg-gray-100 text-gray-800 border-gray-200'} border`;
};


const MobileApplicationCard = memo(({ 
  application, 
  onRowClick, 
  selectedApplicationId,
  selectedMonth
}: MobileApplicationCardProps) => {
  const { user } = useAuth();
  // Show audio player only if user has feature_access_id = 1 AND 2 (both required)
  const shouldShowAudio = (user?.feature_access_ids?.includes(1) && user?.feature_access_ids?.includes(2)) ?? false;
  
  // Use the same data flow as desktop ApplicationRow - directly from application object
  const displayStatus = application.status || 'Unpaid';
  const displayPtpDate = application.ptp_date;
  
  const [expanded, setExpanded] = useState(false);
  const comments = (application.recent_comments || []).slice(0, expanded ? 2 : 1);
  const vehicleTagInfo = getVehicleStatusTagInfo(application.vehicle_status);
  const showNach = application.nach_status !== undefined && application.nach_status !== null;
  const nachActive = Number(application.nach_status) === 1;
  const showNachReason = showNach && !nachActive && !!application.reason;

  return (
    <Card 
      className={`cursor-pointer transition-all duration-200 hover:shadow-md active:scale-[0.98] bg-white border border-gray-200 ${
        selectedApplicationId === application.id ? 'ring-2 ring-blue-500 shadow-md' : 'hover:shadow-lg'
      }`}
      onClick={(e) => {
        // Don't navigate if user is selecting text (copying)
        const selection = window.getSelection();
        if (selection && selection.toString().length > 0) {
          return;
        }
        onRowClick(application);
      }}
    >
      <CardContent className="p-3.5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-start gap-1.5">
            <button
              type="button"
              aria-expanded={expanded}
              aria-label={expanded ? "Hide loan details" : "Show loan details"}
              onClick={(event) => {
                event.stopPropagation();
                setExpanded((open) => !open);
              }}
              className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500"
            >
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
            <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold leading-6 text-gray-900 break-words">{application.applicant_name}</h3>
            <p className="mt-0.5 text-xs text-gray-500">ID: {application.applicant_id}</p>
            <div className="mt-1.5 flex flex-wrap items-center gap-1">
              <LoanTypeBadge loanType={application.loan_type} />
              {vehicleTagInfo && (
                <span
                  className={`inline-flex h-5 items-center rounded-full border px-1.5 text-[10px] font-semibold leading-none ${vehicleTagInfo.bgColor} ${vehicleTagInfo.color}`}
                  title={vehicleTagInfo.title}
                >
                  {vehicleTagInfo.initials}
                </span>
              )}
              {application.special_case_tags?.map((tag, index) => {
                if (!tag) return null;
                const tagInfo = getTagInitials(tag);
                if (!tagInfo) return null;
                return (
                  <span
                    key={index}
                    className={`inline-flex h-5 items-center rounded-full border px-1.5 text-[10px] font-semibold leading-none ${tagInfo.bgColor} ${tagInfo.color}`}
                    title={String(tag)}
                  >
                    {tagInfo.initials}
                  </span>
                );
              })}
            </div>
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1.5">
            <Badge className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(displayStatus)}`}>
              {displayStatus}
            </Badge>
            {application.applicant_mobile ? (
              <CallButton
                name="Call"
                displayName={application.applicant_name}
                phone={application.applicant_mobile}
                loanApplicationId={application.loan_id ?? application.applicant_id}
                loanId={application.loan_id}
                repaymentId={application.payment_id}
                contactType={1}
                variant="default"
                size="sm"
                className="h-8 shrink-0 bg-blue-600 px-3 text-white hover:bg-blue-700 border-0"
              />
            ) : (
              <Button
                variant="default"
                size="sm"
                className="h-8 shrink-0 bg-blue-600 px-3 text-white hover:bg-blue-700 border-0"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Phone className="mr-1 h-3 w-3" />
                <span className="text-xs">No Number</span>
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
          <Fact label="Overdue as of today">{formatOverdue(application.app_overdue_as_of_today)}</Fact>
          <Fact label="PTP date">{displayPtpDate ? formatPtpDate(displayPtpDate) : 'Not set'}</Fact>
          <Fact label="Amount collected">
            {formatCurrency(application.amount_collected || 0)}
          </Fact>
          <Fact label="EMI">
            {application.emi_amount != null && !isNaN(application.emi_amount)
              ? formatCurrency(application.emi_amount)
              : 'N/A'}
          </Fact>
          <Fact label="Branch">{application.branch_name || 'N/A'}</Fact>
          <Fact label="RM">{application.rm_name || 'N/A'}</Fact>
          <Fact label="TL">{application.tl_name ?? application.team_lead ?? 'N/A'}</Fact>
          <Fact label="DPD Bucket">{application.current_dpd_bucket ?? 'X'}</Fact>
        </div>

        {expanded && (
          <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 rounded-xl bg-[#E7F1FB] px-3 py-3">
            <Fact label="LMS as of 1st">{formatOverdue(application.lms_overdue_as_of_1st)}</Fact>
            <Fact label="LMS as of today">{formatOverdue(application.lms_overdue_as_of_today)}</Fact>
            <Fact label="Lender">{application.lender_name || application.lender || 'N/A'}</Fact>
            <Fact label="RC number">{application.rc_number || 'N/A'}</Fact>
            <Fact label="Tenure">{application.tenure != null ? `${application.tenure} months` : 'N/A'}</Fact>
            {showNach && (
              <Fact label="NACH status">
                {nachActive ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <X className="h-4 w-4 text-red-600" />
                )}
              </Fact>
            )}
            {showNachReason && (
              <div className="col-span-2">
                <Fact label="NACH reason">{application.reason}</Fact>
              </div>
            )}
          </div>
        )}

        <div className="border-t border-gray-100 pt-2">
          {comments.length > 0 ? (
            <div className="space-y-2">
              {comments.map((comment, index) => (
                <div key={index} className="border-l-2 border-blue-200 pl-2.5">
                  <div className="truncate text-sm font-medium leading-5 text-blue-700">
                    {comment.user_name || 'Unknown user'}
                  </div>
                  {comment.content && (
                    <div className={expanded ? "text-xs leading-4 text-gray-700 break-words" : "truncate text-xs leading-4 text-gray-700"}>
                      {comment.content}
                    </div>
                  )}
                  {comment.play_url && shouldShowAudio && (
                    <div className="mt-1.5">
                      <AudioPlayer audioUrl={comment.play_url} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs italic text-gray-400">No comments yet</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
});

MobileApplicationCard.displayName = "MobileApplicationCard";

export default MobileApplicationCard;
